import React, { useEffect, useRef, useState } from 'react';
import { useT } from '../../i18n/LanguageContext';
import distributorService from '../../services/distributorService';
import useShippingRequest from '../../hooks/useShippingRequest';
import './CashBank.css';

const read = (response, id) => {
  const row = response?.data?.data;
  if (
    !row ||
    row.schemaVersion !== 1 ||
    row.distributorId !== id ||
    !Number.isSafeInteger(row.revision) ||
    row.revision < 0 ||
    !['available', 'paused', 'otherRestrictions'].every((key) => typeof row[key] === 'boolean') ||
    ![null, 'provider_verified', 'admin_review_v1'].includes(row.policy) ||
    (row.latest !== null &&
      (!row.latest ||
        typeof row.latest.requestKey !== 'string' ||
        typeof row.latest.reference !== 'string' ||
        typeof row.latest.reason !== 'string' ||
        typeof row.latest.paused !== 'boolean' ||
        !Number.isFinite(Date.parse(row.latest.createdAt))))
  )
    throw new Error('Invalid dispute control');
  return row;
};

export default function CashDisputeControl({
  distributorId,
  disabled = false,
  onLockChange,
  onUpdated,
}) {
  const t = useT();
  const [state, setState] = useState(null),
    [editing, setEditing] = useState(false);
  const [reason, setReason] = useState(''),
    [reference, setReference] = useState('');
  const [confirmed, setConfirmed] = useState(false),
    [busy, setBusy] = useState(false);
  const [uncertain, setUncertain] = useState(false),
    [error, setError] = useState('');
  const [notice, setNotice] = useState(''),
    [retry, setRetry] = useState(0);
  const saving = useRef(false),
    callbacks = useRef({ onLockChange, onUpdated });
  callbacks.current = { onLockChange, onUpdated };
  const { begin, version } = useShippingRequest(`cash-dispute:${distributorId}`);
  useEffect(() => {
    callbacks.current.onLockChange?.(editing || busy || uncertain);
    return () => callbacks.current.onLockChange?.(false);
  }, [editing, busy, uncertain]);
  useEffect(() => {
    const request = begin();
    setState(null);
    setEditing(false);
    setBusy(true);
    setError('');
    Promise.resolve(
      distributorService.adminCashDisputeControl(distributorId, { signal: request.signal })
    )
      .then((response) => {
        if (!request.isCurrent()) return;
        setState(read(response, distributorId));
        setUncertain(false);
        setEditing(false);
        saving.current = false;
        if (retry) callbacks.current.onUpdated?.();
      })
      .catch(() => {
        if (request.isCurrent())
          setError('Dispute status could not load. Reload before making changes.');
      })
      .finally(() => {
        if (request.isCurrent()) setBusy(false);
      });
  }, [begin, version, distributorId, retry]);

  const save = async (event) => {
    event.preventDefault();
    if (disabled || busy || uncertain || saving.current || !state?.available || !editing) return;
    if (
      reason.trim().length < 5 ||
      reason.length > 500 ||
      Array.from(reason).some((char) => char.charCodeAt(0) < 32 || char.charCodeAt(0) === 127) ||
      !/^[A-Za-z0-9][A-Za-z0-9 ._:/#-]{2,119}$/.test(reference) ||
      (state.paused && !confirmed)
    ) {
      setError('Enter a dispute reference and reason. Confirm resolution before removing a hold.');
      return;
    }
    const requestKey = window.crypto?.randomUUID?.();
    if (!requestKey) {
      setError('A secure request reference is unavailable. Reload this page.');
      return;
    }
    const request = begin();
    saving.current = true;
    setBusy(true);
    setError('');
    setNotice('');
    try {
      const saved = read(
        await distributorService.saveAdminCashDisputeControl(distributorId, {
          requestKey,
          revision: state.revision,
          paused: !state.paused,
          reason: reason.trim(),
          reference,
          confirmedResolved: state.paused,
        }),
        distributorId
      );
      if (!request.isCurrent()) return;
      if (
        saved.revision !== state.revision + 1 ||
        saved.paused === state.paused ||
        saved.latest?.requestKey !== requestKey
      )
        throw new Error('Unconfirmed hold');
      setState(saved);
      setEditing(false);
      setReason('');
      setReference('');
      setConfirmed(false);
      setNotice('Dispute hold saved. Balance unchanged.');
      callbacks.current.onUpdated?.();
    } catch {
      if (request.isCurrent()) {
        setUncertain(true);
        setError('The change could not be confirmed. Reload dispute status before continuing.');
      }
    } finally {
      if (request.isCurrent()) {
        saving.current = false;
        setBusy(false);
      }
    }
  };
  return (
    <fieldset className="me-cash me-cash-card">
      <legend>{t('Payment disputes')}</legend>
      <div className="me-cash-heading">
        <h2>{t(state?.paused ? 'Manual dispute hold active' : 'Admin dispute review')}</h2>
        <button
          type="button"
          disabled={disabled || busy || (editing && !uncertain)}
          onClick={() => setRetry((value) => value + 1)}
        >
          {t('Reload dispute status')}
        </button>
      </div>
      <p>
        {t(
          'Review disputes in QuickBooks. Pause spending here while a case is investigated. Payment and refund checks continue automatically.'
        )}
      </p>
      {!state && busy && <p role="status">{t('Loading dispute status...')}</p>}
      {error && <p role="alert">{t(error)}</p>}
      {notice && <p role="status">{t(notice)}</p>}
      {state?.otherRestrictions && (
        <p>
          {t(
            'Automatic payment restrictions also apply. Removing this manual hold will not clear them.'
          )}
        </p>
      )}
      {state?.latest && (
        <p>
          {t('Last update')}: {new Date(state.latest.createdAt).toLocaleString()} ·{' '}
          {state.latest.reference}
          <br />
          {state.latest.reason}
        </p>
      )}
      {state && !state.available && (
        <p>{t('Save the distributor bank configuration before adding a dispute hold.')}</p>
      )}
      {state?.available && !editing && (
        <button
          type="button"
          disabled={disabled || busy || uncertain}
          onClick={() => {
            setEditing(true);
            setReason('');
            setReference('');
            setConfirmed(false);
            setNotice('');
          }}
        >
          {t(state.paused ? 'Resolve manual hold' : 'Pause spending for a dispute')}
        </button>
      )}
      {editing && (
        <form onSubmit={save}>
          <fieldset disabled={disabled || busy || uncertain}>
            <div className="me-cash-grid">
              <label>
                {t('Dispute reference')}
                <input
                  required
                  maxLength={120}
                  value={reference}
                  onChange={(e) => setReference(e.target.value)}
                />
              </label>
              <label>
                {t('Reason')}
                <input
                  required
                  minLength={5}
                  maxLength={500}
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                />
              </label>
            </div>
            <p>{t('Use a case reference. Do not enter card numbers or patient information.')}</p>
            {state.paused && (
              <label className="me-cash-dispute-confirm">
                <input
                  type="checkbox"
                  checked={confirmed}
                  onChange={(e) => setConfirmed(e.target.checked)}
                />
                {t(
                  'I verified that this dispute hold can be removed. Other payment restrictions stay in place.'
                )}
              </label>
            )}
            <div className="me-cash-actions">
              <button
                type="button"
                onClick={() => {
                  setEditing(false);
                  setError('');
                }}
              >
                {t('Cancel')}
              </button>
              <button
                type="submit"
                className="me-cash-primary"
                disabled={state.paused && !confirmed}
              >
                {t(busy ? 'Saving...' : state.paused ? 'Remove manual hold' : 'Pause spending')}
              </button>
            </div>
          </fieldset>
        </form>
      )}
    </fieldset>
  );
}
